import { isPlatformServer } from '@angular/common';
import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component, computed,
  DestroyRef,
  effect,
  inject,
  PLATFORM_ID, Signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { FooterComponent } from '@app/core/footer/footer.component';
import { HeaderComponent } from '@app/core/header/header.component';
import { AuthService } from '@app/core/shared/auth.service';
import { ToastsAreaComponent } from '@app/core/toasts-area/toasts-area.component';
import { NgbDropdownConfig, NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';
import { FavoriteService } from '@shared/services/favorite.service';
import { SeoService } from '@shared/services/seo.service';
import { UserStoreService } from '@shared/stores/user-store.service';
import { SectionLoaderComponent } from '@shared/ui-components/section-loader/section-loader.component';
import { filter, tap } from 'rxjs';

@Component({
  selector: 'evland-root',
  templateUrl: './app.component.html',
  styleUrls: [ './app.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    RouterOutlet,
    HeaderComponent,
    ToastsAreaComponent,
    FooterComponent,
    SectionLoaderComponent,
  ],
})
export class AppComponent {
  private authService = inject(AuthService);
  private destroyRef = inject(DestroyRef);
  private favoriteService = inject(FavoriteService);
  private matIconRegistry = inject(MatIconRegistry);
  private ngbDropdownConfig = inject(NgbDropdownConfig);
  private ngbTooltipConfig = inject(NgbTooltipConfig);
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private sanitizer = inject(DomSanitizer);
  private seoService = inject(SeoService);
  private userStore = inject(UserStoreService);

  isAuthReady = this.authService.ready;

  private canonicalUrlEl: HTMLLinkElement;

  constructor() {
    this.initNgbConfigs();
    this.initIcons();

    if (isPlatformServer(this.platformId)) return;

    this.initScrollToTop();
    this.initCanonicalUrl();

    this.initEffects();
  }

  private initCanonicalUrl(): void {
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(event => event instanceof NavigationEnd),
        tap(() => {
          if (this.canonicalUrlEl) this.canonicalUrlEl.remove();
        }),
        filter(() => window.location.href.includes('?')),
      )
      .subscribe({
        next: () => this.canonicalUrlEl = this.seoService.createCanonicalUrl(window.location.href.split('?')[0])
      });
  }

  private initEffects(): void {
    effect(() => {
      const currentUser = this.userStore.currentS();

      this.favoriteService.clearFavorites();

      if (!currentUser) return;

      this.favoriteService.init();
    });
  }

  private initIcons(): void {
    const domain = (isPlatformServer(this.platformId)) ? 'http://localhost:4000/' : '';
    const customIconsUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`${domain}assets/icons/custom-icons.svg`);
    const iconlyIconsUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`${domain}assets/icons/iconly-icons.svg`);
    const saxIconsUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`${domain}assets/icons/sax-icons.svg`);
    const socialIconsUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`${domain}assets/icons/social-icons.svg`);

    this.matIconRegistry.addSvgIconSetInNamespace('custom', customIconsUrl);
    this.matIconRegistry.addSvgIconSetInNamespace('iconly', iconlyIconsUrl);
    this.matIconRegistry.addSvgIconSetInNamespace('sax', saxIconsUrl);
    this.matIconRegistry.addSvgIconSetInNamespace('social', socialIconsUrl);
  }

  private initNgbConfigs(): void {
    this.ngbDropdownConfig.container = 'body';
    this.ngbTooltipConfig.container = 'body';
  }

  private initScrollToTop(): void {
    this.router.events.pipe(
      takeUntilDestroyed(this.destroyRef),
      filter(e => e instanceof NavigationEnd)
    ).subscribe(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }
}

